import el from "@cypherpotato/el";
import { api } from "../../api";
import { runOperation } from "../../ui/operation";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { Section } from "../../components/layout";
import { CommandBar } from "../../components/command-bar";
import { Button, ButtonRow, DangerButton } from "../../components/button";
import { Icon } from "../../components/icon";
import page from "page";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../components/table";
import { CopiableText, MutedText, Paragraph } from "../../components/text";
import { ellipsis } from "../../util";
import { Card, CardContent, CardHeader } from "../../components/card";
import { Dropdown, DropdownDivider, DropdownItem } from "../../components/dropdown";
import { showFlyout } from "../../ui/flyout";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { FileInput } from "../../components/file-input";
import { getSession } from "../../session";
import { Warning } from "../../components/warn";
import { FormControl, TextInput } from "../../components/form";
import { throttledCall } from "../../ui/throttle";

export default function (ctx) {

    const skills = el.state.create(null);
    const filter = el.state.create('');

    async function fetchSkills() {
        const response = await api.getJson('/api/v1/skills?' + new URLSearchParams({
            filter: filter.value
        }).toString());
        skills.value = response.data;
    }

    runOperation("Loading skills...", fetchSkills);

    return Dashboard(
        DashboardTitle(
            "Skills",
            "Manage your AI skills here."
        ),
        Section(
            CommandBar(
                Button(Icon("add-line"), "New skill", {
                    onClick() {
                        page("/dashboard/skills/create");
                    }
                }),
                Dropdown(
                    "Manage",
                    DropdownItem("Import skills", Icon("download-2-line"), {
                        onClick() {
                            displayFormControl({
                                title: "Import skills",
                                message: Paragraph(
                                    "Select a JSONL file containing skills to import. ",
                                    "Each line must be a JSON object with 'slug' and 'instructions' at minimum."
                                ),
                                formControlItem: FileInput({ accept: ".jsonl" }),
                                validate: [
                                    [x => x != null, "You must attach a file to continue."]
                                ],
                                onConfirm(value) {
                                    runOperation("Importing skills...", async () => {
                                        const formData = new FormData();
                                        formData.append("skills", value);

                                        const response = await api.fetch('/api/v1/skills/management/import', {
                                            method: 'POST',
                                            body: formData
                                        });
                                        await api.parseBody(response);
                                        await fetchSkills();
                                    });
                                }
                            });
                        }
                    }),
                    DropdownItem("Export skills", Icon("upload-2-line"), {
                        onClick() {
                            const sessionKey = getSession().sessionKey;
                            window.open(window.env.apiEndpoint + `/api/v1/skills/management/export.jsonl?api-key=${sessionKey}`, "_blank");
                        }
                    }),
                    DropdownDivider(),
                    DropdownItem("Delete all skills", Icon("delete-bin-5-line"), {
                        onClick() {
                            displayAlert({
                                title: "Delete all skills",
                                type: "alert-warning",
                                message: el.fragment(
                                    Paragraph("This is a destructive and irreversible action. All your skills will be permanently deleted."),
                                    Paragraph("It is highly recommended that you export your skills before proceeding."),
                                    Warning(
                                        Paragraph("Gateways that use these skills will lose access to the removed skills.")
                                    )
                                ),
                                buttons: [
                                    {
                                        label: "Cancel",
                                        onClick(event) {
                                            event.target.closest("dialog").dismiss();
                                        }
                                    },
                                    {
                                        label: "Delete all skills",
                                        onClick(event) {
                                            event.target.closest("dialog").dismiss();
                                            runOperation("Deleting all skills...", async () => {
                                                await api.fetch('/api/v1/skills/management/clear', { method: 'DELETE' });
                                                await fetchSkills();
                                            });
                                        }
                                    }
                                ]
                            });
                        }
                    })
                )
            )
        ),
        Section(
            CommandBar(
                FormControl(null,
                    TextInput("skillFilter", "Filter skills...", {
                        onInput(event) {
                            throttledCall(async () => {
                                filter.value = event.target.value;
                                await fetchSkills();
                            }, 300);
                        }
                    })
                )
            )
        ),
        Section(
            Card(
                { class: "bg-card" },
                CardHeader("My skills"),
                CardContent(
                    Table(
                        { class: "hoverable" },
                        TableHeader(
                            TableHeaderCell("Id"),
                            TableHeaderCell("Slug"),
                            TableHeaderCell("Description"),
                            TableHeaderCell("Allowed tools"),
                            TableHeaderActionCell("Actions")
                        ),
                        el.state.listening([skills], () =>
                            TableBody(
                                skills.value?.map(skill =>
                                    TableRow(
                                        TableCell(
                                            CopiableText(`#${skill.id.substr(-4)}`, skill.id)
                                        ),
                                        TableCell(skill.slug),
                                        TableCell(ellipsis(skill.description, 120)),
                                        TableCell(skill.allowedToolsCount && `${skill.allowedToolsCount} tool${skill.allowedToolsCount > 1 ? 's' : ''}` || MutedText("—")),
                                        TableActionCell(
                                            ButtonRow(
                                                Button(Icon("pencil-line"), "Edit", {
                                                    onClick() {
                                                        page('/dashboard/skills/' + skill.id);
                                                    }
                                                }),
                                                Dropdown(null,
                                                    DropdownItem(
                                                        "Delete skill", Icon("delete-bin-5-line"),
                                                        {
                                                            onClick() {
                                                                showFlyout(
                                                                    Paragraph("Are you sure you want to delete this skill? This action cannot be undone."),
                                                                    DangerButton("Yes, delete this skill", {
                                                                        onClick() {
                                                                            runOperation("Deleting skill...", async () => {
                                                                                await api.deleteJson('/api/v1/skills/' + skill.id);
                                                                                await fetchSkills();
                                                                            });
                                                                        }
                                                                    })
                                                                )
                                                            }
                                                        }
                                                    )
                                                )
                                            )
                                        )
                                    )
                                )?.nullWhenEmpty() || DefaultRow("No skills found.")
                            )
                        )
                    )
                )
            )
        )
    )
}
